import React, { useState, useEffect, useRef } from 'react';
import { X, FileText, LogOut, User, Mail, Phone, Users, DollarSign, TrendingUp } from 'lucide-react';

export default function AdminSidebar({ 
  isOpen, 
  onClose, 
  userData, 
  onLogout,
  onPartnersManagementClick,
  onRechargeRequestsClick,
  onMargeB2BControlClick,
  onTransactionHistoryClick 
}) {
  const sidebarRef = useRef(null);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (isOpen) {
      fetchPendingCount();
    }
  }, [isOpen]);

  const fetchPendingCount = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recharge-requests/pending/count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPendingCount(data.count);
      }
    } catch (error) {
      console.error('Error fetching pending count:', error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target) && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
        style={{ opacity: isOpen ? 1 : 0 }}
      />

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className="fixed top-0 right-0 h-full w-96 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto"
        style={{ transform: isOpen ? 'translateX(0)' : 'translateX(100%)' }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Admin Panel</h2>
            <button
              onClick={onClose}
              className="hover:bg-white/20 p-2 rounded-full transition"
            >
              <X size={24} />
            </button>
          </div>

          {/* Admin Info */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <User size={16} />
              <span className="text-sm">{userData.prenom} {userData.nom}</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail size={16} />
              <span className="text-sm">{userData.email}</span>
            </div>
            {userData.tel && (
              <div className="flex items-center gap-2">
                <Phone size={16} />
                <span className="text-sm">{userData.tel}</span>
              </div>
            )}
            <div className="mt-3 px-3 py-1 bg-red-800 rounded-full text-xs font-bold inline-block">
              ADMINISTRATEUR
            </div>
          </div>
        </div>

        {/* Admin Menu Section */}
        <div className="p-6 space-y-3">
          <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-4">
            Gestion Admin
          </h3>

          {/* Partners Management */}
          <button
            onClick={() => {
              onClose();
              if (onPartnersManagementClick) {
                onPartnersManagementClick();
              } else {
                console.log('❌ onPartnersManagementClick is undefined!');
              }
            }}
            className="w-full bg-[#2B4979] hover:bg-[#1e3454] text-white font-semibold py-3 rounded-lg transition flex items-center justify-center gap-2"
          >
            <Users size={18} />
            Gestion des Partenaires
          </button>

          {/* Recharge Requests */}
          <button
            onClick={() => {
              onClose();
              if (onRechargeRequestsClick) onRechargeRequestsClick();
            }}
            className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 rounded-lg transition flex items-center justify-center gap-2 relative"
          >
            <DollarSign size={18} />
            Demandes de Rechargement
            
            {/* ✅ Notification Badge */}
            {pendingCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
                {pendingCount}
              </span>
            )}
          </button>

          {/* Marge B2B Control */}
          <button
            onClick={() => {
              onClose();
              if (onMargeB2BControlClick) {
                onMargeB2BControlClick();
              } else {
                console.log('❌ onMargeB2BControlClick is undefined!');
              }
            }}
            className="w-full bg-[#FF7B47] hover:bg-[#ff6a30] text-white font-semibold py-3 rounded-lg transition flex items-center justify-center gap-2"
          >
            <TrendingUp size={18} />
            Contrôle Marges B2B
          </button>

        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 my-2"></div>

        {/* Transaction History */}
        <div className="p-6 border-b">
          <button
            onClick={() => {
              onClose();
              if (onTransactionHistoryClick) onTransactionHistoryClick();
            }}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 rounded-lg transition flex items-center justify-center gap-2"
          >
            <FileText size={18} />
            Historique des Transactions
          </button>
        </div>

        {/* Logout Button */}
        <div className="p-6">
          <button
            onClick={() => {
              onClose();
              if (onLogout) onLogout();
            }}
            className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-3 rounded-lg transition flex items-center justify-center gap-2"
          >
            <LogOut size={18} />
            Déconnexion
          </button>
        </div>
      </div>
    </>
  );
}
